import {AccordionDetails, Box, Button, MenuItem, TextField} from "@mui/material"
import React, {ChangeEvent, Reducer, useEffect, useReducer, useState} from "react"
import {AvantExpansionPanel} from "../../../../../generic-components/AvantExpansionPanel"
import {useReportUrlFilters} from "../../../hooks/useReportUrlFilters"
import {TestTakerSearchTable} from "./TestTakerSearchTable"
import {
    getResetFormState, getSubGroupHeader,
} from "../TestTakerSearchUtils";
import {theme} from "../../../../../../styles/MuiThemes";
import {DateRangePicker} from "@mui/x-date-pickers-pro/DateRangePicker";
import moment from "moment";
import {SingleInputDateRangeField} from "@mui/x-date-pickers-pro/SingleInputDateRangeField";
import {fetchTakeStateCounts, TakeStateCounts, TakeStateCountsDTO} from "../../aggregate-charts/TakeStateCounts"
import {FormState, LanguageSelectInfo, RootFormStateProps, TakeRecord, ProductDTO} from "../TestTakerSearchTypes"
import {
    TestTakerSearchApi
} from "../TestTakerSearchApi"
import ApiService from "../../../../../../services/ApiService"

const FILTER_WIDTH = theme.spacing(20)

/** Value to indicate a filter that has not yet been selected */
const SELECT_A_VALUE = -1

function reducer(state: FormState, action: FormAction): FormState {
    switch (action.type) {
        case "updated_first_name":
            return {...state, firstName: action.payload}
        case "updated_last_name":
            return {...state, lastName: action.payload}
        case "updated_login_name":
            return {...state, loginName: action.payload}
        case "updated_test_code":
            return {...state, testCode: action.payload}
        case "selected_product":
            return {...state, productId: action.payload}
        case "selected_subgroup":
            return {...state, selectedSubGroup: action.payload}
        case "selected_language":
            return {...state, selectedLanguage: action.payload}
        case "selected_participation_code":
            return {...state, selectedParticipationCode: action.payload}
        case "loaded_subgroup_options":
            return {
                ...state,
                subGroupOptions: action.payload.subGroupOptions,
                subGroupHeader: action.payload.subGroupHeader
            }
        case "loaded_language_options":
            return {...state, languageOptions: action.payload.languageOptions}
        case "loaded_participation_codes":
            return {...state, participationCodes: action.payload.participationCodes}
        case "loaded_product_options":
            return {...state, productOptions: action.payload.productOptions}
        case "changed_start_date":
            return {...state, startDate: action.payload}
        case "changed_end_date":
            return {...state, endDate: action.payload}
        case "clear_filters":
            return getResetFormState({...action.payload, mode: "clear"})
        case "reset_filters":
            return getResetFormState({...action.payload, mode: "reset"})
        default:
            return state
    }
}

type FormAction =
    | { type: "updated_first_name", payload: string }
    | { type: "updated_last_name", payload: string }
    | { type: "updated_login_name", payload: string }
    | { type: "updated_test_code", payload: string }
    | { type: "selected_product", payload: number }
    | { type: "selected_subgroup", payload: number }
    | { type: "selected_language", payload: number }
    | { type: "selected_participation_code", payload: number }
    | { type: "loaded_language_options", payload: { languageOptions: LanguageSelectInfo[] } }
    | {
    type: "loaded_subgroup_options",
    payload: { subGroupOptions: FormState["subGroupOptions"], subGroupHeader: FormState["subGroupHeader"] }
}
    | { type: "loaded_product_options", payload: { productOptions: FormState["productOptions"] } }
    | { type: "loaded_participation_codes", payload: { participationCodes: FormState["participationCodes"] } }
    | { type: "changed_start_date", payload: moment.Moment | null }
    | { type: "changed_end_date", payload: moment.Moment | null }
    | { type: "clear_filters", payload: RootFormStateProps }
    | { type: "reset_filters", payload: RootFormStateProps }

export const TestTakerSearchPage = () => {
    const reportUrlFilters = useReportUrlFilters()
    const defaultFormState: FormState = {
        subGroupHeader: undefined,
        subGroupOptions: [],
        languageOptions: [],
        selectedSubGroup: reportUrlFilters.groupId,
        selectedLanguage: -1,
        selectedParticipationCode: undefined,
        productId: -1,
        productOptions: [],
        participationCodes: [],
        firstName: "",
        lastName: "",
        loginName: "",
        testCode: "",
        startDate: moment().subtract(1, 'year').month(7).date(1),
        endDate: moment()
    }

    const [formState, dispatch] = useReducer<Reducer<FormState, FormAction>>(reducer, defaultFormState)
    const [tableData, setTableData] = useState<TakeRecord[]>([])
    const [takeStateCounts, setTakeStateCounts] = useState<TakeStateCountsDTO>({
        "not_started": 0,
        "in_progress": 0,
        "completed": 0,
        "graded": 0,
        "total": 0,
        "not_rateable": 0
    })

    const [rootFormState, setRootFormState] = useState<RootFormStateProps>({
        rootGroupId: reportUrlFilters.groupId,
        rootSubGroupOptions: [],
        rootLanguageOptions: [],
        rootProductOptions: [],
        rootSubGroupHeader: undefined,
        rootParticipationCodes: []
    })

    const [loading, setLoading] = useState<boolean>(false)

    async function getLocations() {
        const subGroups = await TestTakerSearchApi.getSubGroups(reportUrlFilters.groupId)
        const subGroupHeader = getSubGroupHeader(subGroups)
        dispatch({
                type: "loaded_subgroup_options",
                payload: {
                    subGroupOptions: subGroups,
                    subGroupHeader: subGroupHeader
                }
        })

        /** If group related rootFormState is not set, set them. */
        if (rootFormState.rootSubGroupOptions.length === 0) {
            setRootFormState(rootFormState => {
                return {...rootFormState, rootSubGroupOptions: subGroups, rootSubGroupHeader: subGroupHeader}
            })
        }
    }

    async function getLanguages() {
        const languageOptions = await TestTakerSearchApi.getLanguageOptions(formState.selectedSubGroup)
        dispatch({
            type: "loaded_language_options",
            payload: {languageOptions: languageOptions}
        })

        /** If the language option list is reset we should also reset the current value */
        dispatch({
            type: "selected_language",
            payload: SELECT_A_VALUE,
        })

        /** If rootLanguageOptions are unset, set them. */
        if (rootFormState.rootLanguageOptions.length === 0) {
            setRootFormState(rootFormState => {
                return {...rootFormState, rootLanguageOptions: languageOptions}
            })
        }
    }

    async function getProducts() {
        const productOptions = await TestTakerSearchApi.getProductOptions(formState.selectedSubGroup)
        dispatch({
            type: "loaded_product_options",
            payload: {productOptions: productOptions}
        })

        /** If the product options list is reset we should also reset the current value */
        dispatch({
            type: "selected_product",
            payload: SELECT_A_VALUE
        })

        /** If rootProductOptions are unset, set them */
        if (rootFormState.rootProductOptions.length === 0) {
            setRootFormState(rootFormState => {
                return {...rootFormState, rootProductOptions: productOptions}
            })
        }
    }

    async function getParticipationCodes() {
        const participationCodes = await ApiService.getParticipationCodes(formState.selectedSubGroup)
        dispatch({
            type: "loaded_participation_codes",
            payload: {participationCodes: participationCodes}
        })

        setRootFormState(rootFormState => {
            return {...rootFormState, rootParticipationCodes: participationCodes }
        })
    }

    async function loadTableData() {
        setLoading(true)
        const tableData = await TestTakerSearchApi.findReportTableData2025({...reportUrlFilters, ...formState})
        setTableData(tableData)
        setLoading(false)
    }

    useEffect(() => {
        getLocations()
        getParticipationCodes()
    }, [])

    useEffect(() => {
        getLanguages()
        getProducts()
    }, [formState.selectedSubGroup])

    useEffect(() => {
        fetchTakeStateCounts(reportUrlFilters.groupId)
            .then((counts) => setTakeStateCounts(counts))
    }, [])

    return (
        <>
            <AvantExpansionPanel title={"Test Taker Status Summary"}>
                <AccordionDetails sx={{display: "flex", flexWrap: "wrap", flexDirection: "column", gap: "8px"}}>
                    <TakeStateCounts statusCounts={takeStateCounts}/>
                </AccordionDetails>
            </AvantExpansionPanel>

            <AvantExpansionPanel title={"Test Taker Search"}>
                <AccordionDetails sx={{display: "flex", flexWrap: "wrap", flexDirection: "column", gap: "8px"}}>
                    <Box sx={{display: "flex", flexWrap: "wrap", width: "100%", gap: 1}}>
                        {formState.subGroupOptions.length > 0 && formState.subGroupHeader &&
                            <TextField
                                select
                                label={`${formState.subGroupHeader}`}
                                id={"location-select"}
                                data-tst-id={"location-select"}
                                value={formState.selectedSubGroup}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                    dispatch({
                                        type: "selected_subgroup",
                                        payload: Number(e.target.value)
                                    })
                                }}
                                sx={{width: FILTER_WIDTH}}
                            >
                                <MenuItem key={reportUrlFilters.groupId}
                                          value={reportUrlFilters.groupId}>{`Select a ${formState.subGroupHeader}`}</MenuItem>
                                {formState.subGroupOptions
                                    .sort((a, b) => a.name.localeCompare(b.name))
                                    .map(l => <MenuItem key={l.groupId} value={l.groupId}>{l.name}</MenuItem>)
                                }
                            </TextField>
                        }

                        {formState.languageOptions.length > 0 &&
                            <TextField
                                select
                                label={"Language"}
                                id={"language-select"}
                                data-tst-id={"language-select"}
                                value={formState.selectedLanguage}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                    dispatch({
                                        type: "selected_language",
                                        payload: Number(e.target.value)
                                    })
                                }}
                                sx={{width: FILTER_WIDTH}}
                            >
                                <MenuItem key={SELECT_A_VALUE} value={SELECT_A_VALUE}>Select a Language</MenuItem>
                                {formState.languageOptions.sort((a, b) => a.languageName.localeCompare(b.languageName))
                                    .map((language: LanguageSelectInfo) => {
                                        return <MenuItem key={language.contentAreaId}
                                                         value={language.contentAreaId}>{language.languageName}</MenuItem>
                                    })
                                }
                            </TextField>
                        }
                        <TextField
                            select
                            label={"Product"}
                            id={"product-select"}
                            data-tst-id={"product-select"}
                            value={formState.productId}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                dispatch({
                                    type: "selected_product",
                                    payload: Number(e.target.value)
                                })
                            }}
                            sx={{width: FILTER_WIDTH}}
                        >
                            <MenuItem key={SELECT_A_VALUE} value={SELECT_A_VALUE}>Select a Product</MenuItem>
                            {formState.productOptions.sort((a, b) => a.productDisplayName.localeCompare(b.productDisplayName))
                                .map((product: ProductDTO) => {
                                    return <MenuItem key={product.productId}
                                                     value={product.productId}>{product.productDisplayName}</MenuItem>
                                })}
                        </TextField>

                        <TextField
                            label={"Test code"}
                            id={"test-code-filter"}
                            data-tst-id={"test-code-filter"}
                            value={formState.testCode}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => dispatch({
                                type: "updated_test_code",
                                payload: e.target.value
                            })}
                            sx={{width: FILTER_WIDTH}}
                        />

                        <TextField
                            label={"Login name"}
                            id={"login-name-filter"}
                            data-tst-id={"login-name-filter"}
                            value={formState.loginName}
                            onChange={(e) => dispatch({type: "updated_login_name", payload: e.target.value})}
                            sx={{width: FILTER_WIDTH}}
                        />

                        <TextField
                            label={"First name"}
                            id={"first-name-filter"}
                            data-tst-id={"first-name-filter"}
                            value={formState.firstName}
                            onChange={(e) => dispatch({type: "updated_first_name", payload: e.target.value})}
                            sx={{width: FILTER_WIDTH}}
                        />

                        <TextField
                            label={"Last name"}
                            id={"last-name-filter"}
                            data-tst-id={"last-name-filter"}
                            value={formState.lastName}
                            onChange={(e) => dispatch({type: "updated_last_name", payload: e.target.value})}
                            sx={{width: FILTER_WIDTH}}
                        />

                        <DateRangePicker localeText={{start: "Start Date", end: "End Date"}}
                                         sx={{width: 210}}
                                         value={[formState.startDate, formState.endDate]}
                                         onChange={([startDate, endDate]) => {
                                             dispatch({type: "changed_start_date", payload: startDate})
                                             dispatch({type: "changed_end_date", payload: endDate})
                                         }}
                                         slots={{
                                             field: SingleInputDateRangeField,
                                         }}
                                         slotProps={{
                                             textField: {
                                                 InputProps: {
                                                     sx: {textAlign: "center", input: {textAlign: "center"}},
                                                 },
                                             },
                                             shortcuts: {
                                                 items: [
                                                     {
                                                         label: "Last 3 Months",
                                                         getValue: () => [moment().subtract(3, "months"), moment()]

                                                     },
                                                     {
                                                         label: "Last 6 Months",
                                                         getValue: () => [moment().subtract(6, "months"), moment()]
                                                     },
                                                     {
                                                         label: "Last Year",
                                                         getValue: () => [moment().subtract(1, "year"), moment()]
                                                     }
                                                 ]
                                             }
                                         }}
                        />
                        <Button
                            onClick={loadTableData}>
                            Search
                        </Button>
                        <Button variant={"outlined"}
                                onClick={() => dispatch({type: "clear_filters", payload: rootFormState})}>Clear</Button>
                        <Button variant={"outlined"}
                                onClick={() => dispatch({type: "reset_filters", payload: rootFormState})}>Reset</Button>
                    </Box>
                    <TestTakerSearchTable
                        tableData={tableData}
                        loading={loading}
                        participationCodes={formState.participationCodes}/>
                </AccordionDetails>
            </AvantExpansionPanel>
        </>
    )
}
